import * as Icons from '@ant-design/icons';
import useNotification from 'hooks/useNotification';
import { Button, Collapse, Flex, Form, Modal, Space, Typography } from 'antd';
import { CollapsePanelProps } from 'antd/lib';
import { DocumentFieldItemCol } from 'components/documentation/DocumentFieldItemCol';
import { IDocument } from 'interfaces/documentation/document';
import { useCallback, useEffect, useState } from 'react';
import { useUpdate } from '@refinedev/core';

interface DocumentPanelProps extends Omit<CollapsePanelProps, 'header'> {
  document: IDocument;
  isPanelOpen: boolean;
  enrollmentCertificateStatus?: string;
  documentationId?: string;
  onViewFile: (document: IDocument) => void;
  onDocumentUpdated?: () => void;
}

export const DocumentPanel = ({
  document,
  isPanelOpen,
  enrollmentCertificateStatus,
  documentationId,
  onViewFile,
  onDocumentUpdated,
  ...props
}: DocumentPanelProps) => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const { mutateAsync, isLoading: isUpdating } = useUpdate();
  const { showError } = useNotification();
  const [modal, contextHolder] = Modal.useModal();

  // Preencher o form quando começar a editar
  useEffect(() => {
    if (isEditing) {
      const initialValues: Record<string, unknown> = {};

      for (const field of document.attribute_fields) {
        const uniqueFieldName = `${document.id}_${field.name}`;

        initialValues[uniqueFieldName] = field.value;
      }

      form.setFieldsValue(initialValues);
    }
  }, [isEditing, document, form]);

  const handleEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Resetar apenas os campos específicos do documento
      const fieldsToReset = document.attribute_fields.map(
        field => `${document.id}_${field.name}`
      );
      form.resetFields(fieldsToReset);
      setIsEditing(false);
    },
    [document, form]
  );

  const handleUpdateDocument = useCallback(
    async (document: IDocument, formValues: Record<string, unknown>) => {
      if (!documentationId) {
        showError('ID da documentação não encontrado.');
        return;
      }

      try {
        // Criar uma cópia do documento com os novos valores
        const updatedDocument = { ...document };

        // Atualizar os attribute_fields com os novos valores do formulário usando nomes únicos
        updatedDocument.attribute_fields = document.attribute_fields.map(
          field => {
            const uniqueFieldName = `${document.id}_${field.name}`;

            return {
              ...field,
              value: (formValues[uniqueFieldName] as string) || field.value
            };
          }
        );

        // Fazer a requisição para atualizar o documento
        await mutateAsync({
          resource: 'documentations',
          id: `${documentationId}/documents/${document.id}`,
          values: { attribute_fields: updatedDocument.attribute_fields },
          successNotification: () => ({
            type: 'success',
            description: 'Sucesso!',
            message: 'Dados do documento atualizados com sucesso!'
          }),
          errorNotification: () => ({
            type: 'error',
            description: 'Ops!',
            message: 'Não foi possível atualizar os dados do documento.'
          })
        });

        setIsEditing(false);

        // Chamar callback para atualizar os dados
        onDocumentUpdated?.();
      } catch {
        showError('Erro ao atualizar os dados do documento.');
      }
    },
    [documentationId, mutateAsync, showError, onDocumentUpdated]
  );

  const handleSave = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      try {
        const values = await form.validateFields();

        await modal.confirm({
          title: 'Confirmar edição de documentação?',
          width: 600,
          content: (
            <Flex vertical gap={16}>
              <Typography.Text>
                Ao editar esta documentação, ela será atualizada permanentemente
                no sistema. Esta ação será registrada no histórico do sistema.
              </Typography.Text>
              <Typography.Text>Deseja continuar?</Typography.Text>
            </Flex>
          ),
          onOk: async () => {
            await handleUpdateDocument(document, values);
          },
          onCancel: () => {
            setIsEditing(false);
          }
        });
      } catch {
        setIsEditing(false);
      }
    },
    [document, form, handleUpdateDocument, modal, setIsEditing]
  );

  const handleViewFile = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onViewFile(document);
    },
    [document, onViewFile]
  );

  const hasFields = document.attribute_fields.length > 0;
  const canEdit =
    hasFields && enrollmentCertificateStatus !== 'issued' && isPanelOpen;

  return (
    <>
      <Collapse.Panel
        {...props}
        header={document.name}
        className="border-t !rounded-none"
        showArrow={hasFields}
        collapsible={hasFields ? 'header' : 'icon'}
        extra={
          <Space size={8}>
            {canEdit && (
              <Space size={4}>
                <Button
                  icon={<Icons.EditOutlined />}
                  size="small"
                  color="primary"
                  variant="outlined"
                  loading={isUpdating && isEditing}
                  disabled={isUpdating}
                  onClick={isEditing ? handleSave : handleEdit}
                >
                  {isEditing ? 'Salvar edição' : 'Editar'}
                </Button>

                {isEditing && (
                  <Button
                    color="danger"
                    variant="outlined"
                    icon={<Icons.CloseOutlined />}
                    size="small"
                    disabled={isUpdating}
                    onClick={handleCancelEdit}
                  >
                    Cancelar
                  </Button>
                )}
              </Space>
            )}

            <Button
              variant="text"
              icon={<Icons.EyeOutlined />}
              size="small"
              onClick={handleViewFile}
            >
              Visualizar
            </Button>
          </Space>
        }
      >
        {hasFields && (
          <div className="ml-4">
            <Form form={form}>
              {document.attribute_fields.map((field, index) => (
                <DocumentFieldItemCol
                  key={index}
                  field={field}
                  namePrefix={`${document.id}_`}
                  disabled={!isEditing}
                />
              ))}
            </Form>
          </div>
        )}
      </Collapse.Panel>

      {contextHolder}
    </>
  );
};
